/**
 * `payments/{paymentId}`, `wallets/{uid}`, `walletTransactions/{txId}`, `withdrawals/{id}`.
 *
 * Regla de oro (7b): el dinero vuelve por el mismo camino. Un pago tiene N TRAMOS (`legs`):
 * uno por origen (tarjeta, wallet). Cada tramo lleva su importe exacto y su estado; un
 * reembolso parcial se reparte entre tramos con `splitRefund` (../money/refundSplit.ts).
 *
 * Solo Functions escribe en estas colecciones; el cliente solo lee las suyas.
 */
import { z } from 'zod';
import {
  ActorSchema,
  CurrencySchema,
  MoneySchema,
  NonNegativeMoneySchema,
  OrderIdSchema,
  PaymentIdSchema,
  TimestampSchema,
  UserIdSchema,
  WalletTxIdSchema,
} from './primitives.js';

/** `promo`: saldo de premio (2.1 §2, `userPerks` promo_credit); no es dinero del comprador ni retirable. */
export const PAYMENT_LEG_SOURCES = ['card', 'wallet', 'promo'] as const;
export const PaymentLegSourceSchema = z.enum(PAYMENT_LEG_SOURCES);
export type PaymentLegSource = z.infer<typeof PaymentLegSourceSchema>;

/**
 * Estados de un tramo:
 *  pending → authorized → captured → (refunded | partially_refunded)
 *  authorized → released  (anulación de autorización / liberación de retención: nunca se cobró)
 *  pending → released     (el comprador no llegó a pagar: la sesión caducó o la canceló)
 */
export const PAYMENT_LEG_STATUSES = [
  'pending', // sesión de Checkout creada, sin autorizar (ventana de la D3)
  'authorized',
  'captured',
  'released',
  'partially_refunded',
  'refunded',
  'failed',
] as const;
export const PaymentLegStatusSchema = z.enum(PAYMENT_LEG_STATUSES);
export type PaymentLegStatus = z.infer<typeof PaymentLegStatusSchema>;

export const PaymentLegSchema = z.object({
  source: PaymentLegSourceSchema,
  /** Importe cobrado o retenido por este tramo (moneda del comprador). */
  amount: NonNegativeMoneySchema,
  /** Importe ya devuelto a este origen (≤ amount). */
  refunded: NonNegativeMoneySchema,
  status: PaymentLegStatusSchema,
  /** Tarjeta: PaymentIntent con capture_method=manual. */
  stripe_payment_intent_id: z.string().nullable(),
  /** Tarjeta: sesión de Stripe Checkout que creó el PaymentIntent (para confirmar si el webhook tarda). */
  stripe_checkout_session_id: z.string().nullable().optional(),
  /** Tarjeta: método usado (card, apple_pay, google_pay, ideal, blik…), para el desglose del aviso. */
  payment_method_type: z.string().max(40).nullable(),
  card_last4: z.string().length(4).nullable(),
  /** Wallet: id de la retención (`walletTransactions` tipo hold). */
  wallet_hold_tx_id: WalletTxIdSchema.nullable(),
  updated_at: TimestampSchema,
});
export type PaymentLeg = z.infer<typeof PaymentLegSchema>;

export const PAYMENT_STATUSES = [
  'pending', // PaymentIntent creado, sin autorizar
  'authorized', // todos los tramos autorizados/retenidos
  'captured', // vendedor aceptó: capturado / wallet cargado
  'released', // anulado antes de capturar: nunca salió dinero
  'partially_refunded',
  'refunded',
  'transferred', // pago liberado al vendedor (Transfer a Connect)
  'failed',
] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PaymentSchema = z
  .object({
    id: PaymentIdSchema,
    order_id: OrderIdSchema,
    buyer_id: UserIdSchema,
    seller_id: UserIdSchema,
    status: PaymentStatusSchema,
    /** Total cobrado = suma de legs[].amount; moneda del comprador. */
    total: NonNegativeMoneySchema,
    legs: z.array(PaymentLegSchema).min(1).max(3),
    /** Transfer a la cuenta Connect del vendedor (precio del artículo), cuando se libera. */
    stripe_transfer_id: z.string().nullable(),
    stripe_connect_account_id: z.string().nullable(),
    /** Comisión de Stripe real (para la decisión abierta 18.11 sobre quién la absorbe). */
    stripe_fee: MoneySchema.nullable(),
    created_at: TimestampSchema,
    updated_at: TimestampSchema,
  })
  .refine(
    (p) =>
      p.legs.every((l) => l.amount.currency === p.total.currency) &&
      p.legs.reduce((s, l) => s + l.amount.amount, 0) === p.total.amount,
    { message: 'payments: la suma de los tramos debe ser igual al total' },
  )
  .refine((p) => new Set(p.legs.map((l) => l.source)).size === p.legs.length, {
    message: 'payments: como máximo un tramo por origen',
  });
export type Payment = z.infer<typeof PaymentSchema>;
export const paymentPath = (id: string) => `payments/${id}` as const;

// ---------- wallets ----------

/**
 * Decisión D6 (13/10): cada wallet tiene UNA moneda fija (la del usuario al crearse) y todos sus
 * saldos y movimientos van en esa moneda. La conversión ocurre una sola vez, al vender, con el tipo
 * congelado en `orders.seller_amount.fx_rate`; el saldo nunca fluctúa. Cambiar de moneda = retirar
 * todo y abrir otra wallet (nunca se reconvierte un saldo).
 */
export const WalletSchema = z
  .object({
    uid: UserIdSchema,
    currency: CurrencySchema,
    /** Disponible para gastar o retirar. */
    available: NonNegativeMoneySchema,
    /** Retenido por compras pendientes de aceptación (holds). */
    held: NonNegativeMoneySchema,
    /** Pendiente de liberar por ventas no completadas. */
    pending: NonNegativeMoneySchema,
    /** Último tx aplicado; para verificar el ledger (sección 17: saldos iguales antes y después). */
    last_tx_id: WalletTxIdSchema.nullable(),
    updated_at: TimestampSchema,
  })
  .superRefine((w, ctx) => {
    for (const bucket of ['available', 'held', 'pending'] as const) {
      if (w[bucket].currency !== w.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [bucket, 'currency'],
          message: `wallet en ${w.currency}: el saldo ${bucket} no puede estar en ${w[bucket].currency}`,
        });
      }
    }
  });
export type Wallet = z.infer<typeof WalletSchema>;
export const walletPath = (uid: string) => `wallets/${uid}` as const;

export class WalletCurrencyMismatchError extends TypeError {
  constructor(
    public readonly walletCurrency: Wallet['currency'],
    public readonly txCurrency: Wallet['currency'],
    public readonly txId: string,
  ) {
    super(`wallet en ${walletCurrency}: el movimiento ${txId} viene en ${txCurrency}`);
    this.name = 'WalletCurrencyMismatchError';
  }
}

/**
 * Invariante D6: "todo movimiento en la moneda del wallet". Functions lo llama antes de aplicar
 * cualquier `walletTransactions`; una conversión (venta en otra moneda, retiro FX) se hace ANTES,
 * fuera del ledger, y lo que entra ya viene en la moneda del wallet.
 */
export function assertWalletTxCurrency(
  wallet: Pick<Wallet, 'currency'>,
  tx: Pick<WalletTransaction, 'id' | 'amount' | 'balance_after'>,
): void {
  if (tx.amount.currency !== wallet.currency)
    throw new WalletCurrencyMismatchError(wallet.currency, tx.amount.currency, tx.id);
  if (tx.balance_after.currency !== wallet.currency)
    throw new WalletCurrencyMismatchError(wallet.currency, tx.balance_after.currency, tx.id);
}

export const WALLET_TX_TYPES = [
  'topup', // recarga con tarjeta/SEPA
  'hold', // retención por compra (7b.2)
  'hold_release', // liberación de la retención (cancelación)
  'hold_capture', // la retención pasa a cargo (vendedor acepta)
  'sale_pending', // venta: entra en pending
  'sale_release', // pending → available (pago liberado)
  'refund_in', // reembolso que vuelve al wallet
  'withdrawal', // retirada a cuenta bancaria
  'withdrawal_reversal',
  'fee', // tarifas cobradas al vendedor (p. ej. retiro instantáneo)
  'adjustment', // ajuste manual de admin, siempre con reason y auditLog
] as const;
export const WalletTxTypeSchema = z.enum(WALLET_TX_TYPES);

export const WalletTransactionSchema = z.object({
  id: WalletTxIdSchema,
  uid: UserIdSchema,
  type: WalletTxTypeSchema,
  /** Positivo = entra; negativo = sale. Sobre el bucket que indique `bucket`. */
  amount: MoneySchema,
  bucket: z.enum(['available', 'held', 'pending']),
  /** Saldo del bucket DESPUÉS de aplicar (verificación del ledger). */
  balance_after: MoneySchema,
  order_id: OrderIdSchema.nullable(),
  payment_id: PaymentIdSchema.nullable(),
  related_tx_id: WalletTxIdSchema.nullable(),
  /** Obligatoria en cada movimiento (sección 7): `${order}:${type}` o id del evento externo. */
  idempotency_key: z.string().min(1),
  actor: ActorSchema,
  reason: z.string().max(500).optional(),
  created_at: TimestampSchema,
});
export type WalletTransaction = z.infer<typeof WalletTransactionSchema>;

export const WITHDRAWAL_STATUSES = ['requested', 'approved', 'paid', 'rejected', 'failed'] as const;
export const WithdrawalSchema = z
  .object({
    id: z.string().min(1),
    uid: UserIdSchema,
    /** Lo que sale del wallet, en la moneda del wallet (D6). */
    amount: NonNegativeMoneySchema,
    /**
     * D7 (2 %, confirmado 13/10): lo que recibe la cuenta bancaria. Si `payout.currency` ≠
     * `amount.currency`, el recargo FX (`settings/fees.fx_withdrawal_surcharge_percent`) lo paga el
     * vendedor y `fx_rate` queda congelado; si coinciden, `fx_surcharge` es 0 y `fx_rate` es 1.
     */
    payout: NonNegativeMoneySchema,
    fx_rate: z.number().positive(),
    fx_surcharge: NonNegativeMoneySchema,
    /**
     * D7 — requisito de Gabor: antes de una retirada en otra moneda el vendedor ve una ventana con
     * el porcentaje, el importe exacto del recargo y el porqué, y el recordatorio de que retirar en
     * la moneda de su wallet no tiene gasto. Aquí queda CUÁNDO lo aceptó (prueba); `null` solo si
     * no hubo conversión. El callable rechaza una retirada FX sin este campo.
     */
    fx_notice_accepted_at: TimestampSchema.nullable(),
    method: z.enum(['standard', 'instant']),
    status: z.enum(WITHDRAWAL_STATUSES),
    stripe_payout_id: z.string().nullable(),
    /** Retiradas con aprobación admin (como hoy). */
    reviewed_by: UserIdSchema.nullable(),
    reviewed_at: TimestampSchema.nullable(),
    wallet_tx_id: WalletTxIdSchema.nullable(),
    created_at: TimestampSchema,
    updated_at: TimestampSchema,
  })
  .superRefine((w, ctx) => {
    const fx = w.payout.currency !== w.amount.currency;
    if (!fx && w.fx_surcharge.amount !== 0)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fx_surcharge'],
        message: 'withdrawals: sin conversión de moneda no hay recargo FX (D7)',
      });
    if (fx && w.fx_notice_accepted_at === null)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fx_notice_accepted_at'],
        message:
          'withdrawals: una retirada en otra moneda exige el aviso del recargo aceptado (D7)',
      });
  });
export type Withdrawal = z.infer<typeof WithdrawalSchema>;
